import React from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';

/**
 * Folder create/rename dialog
 */
export const FolderNameDialog = ({
  open,
  mode, // 'create' | 'rename'
  folderName,
  parentFolder,
  onNameChange,
  onConfirm,
  onCancel,
}) => {
  const title = mode === 'create' 
    ? (parentFolder ? `Nieuwe submap in "${parentFolder.name}"` : 'Nieuwe map')
    : 'Map hernoemen';

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <Input
            placeholder="Mapnaam"
            value={folderName}
            onChange={(e) => onNameChange(e.target.value)}
            autoFocus
            onKeyDown={(e) => e.key === 'Enter' && onConfirm()}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Annuleren</Button>
          <Button onClick={onConfirm} disabled={!folderName.trim()}>
            {mode === 'create' ? 'Aanmaken' : 'Opslaan'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Folder delete confirmation dialog
 */
export const FolderDeleteDialog = ({
  open,
  folder,
  onConfirm,
  onCancel,
}) => (
  <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Map verwijderen?</AlertDialogTitle>
        <AlertDialogDescription>
          Weet je zeker dat je "{folder?.name}" wilt verwijderen? 
          Alle documenten in deze map worden ook naar de prullenbak verplaatst.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel onClick={onCancel}>Annuleren</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} className="bg-red-600 hover:bg-red-700">
          Verwijderen
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

/**
 * Folder move dialog
 */
export const FolderMoveDialog = ({
  open,
  folder,
  folders,
  targetFolderId,
  onTargetChange,
  onConfirm,
  onCancel,
}) => {
  // Filter out the folder itself and its children from valid targets
  const validTargets = folders.filter(f => 
    f.id !== folder?.id && 
    f.parent_folder_id !== folder?.id &&
    !f.deleted_at
  );

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Map verplaatsen</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <p className="text-sm text-gray-600 mb-2">
            Verplaats "{folder?.name}" naar:
          </p>
          <Select value={targetFolderId || 'root'} onValueChange={onTargetChange}>
            <SelectTrigger>
              <SelectValue placeholder="Selecteer locatie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="root">📁 Hoofdmap (root)</SelectItem>
              {validTargets.map(f => (
                <SelectItem key={f.id} value={f.id}>
                  📁 {f.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Annuleren</Button>
          <Button onClick={onConfirm}>Verplaatsen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Document rename dialog
 */
export const DocumentRenameDialog = ({
  open,
  document,
  documentName,
  onNameChange,
  onConfirm,
  onCancel,
}) => (
  <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Document hernoemen</DialogTitle>
      </DialogHeader>
      <div className="py-4">
        <Input
          placeholder="Document naam"
          value={documentName}
          onChange={(e) => onNameChange(e.target.value)}
          autoFocus
          onKeyDown={(e) => e.key === 'Enter' && onConfirm()}
        />
        <p className="text-xs text-gray-400 mt-2">
          Bestandsextensie: .{document?.extension}
        </p>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Annuleren</Button>
        <Button onClick={onConfirm} disabled={!documentName.trim()}>Opslaan</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

/**
 * Document delete confirmation dialog
 */
export const DocumentDeleteDialog = ({
  open,
  document,
  onConfirm,
  onCancel,
}) => (
  <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Document verwijderen?</AlertDialogTitle>
        <AlertDialogDescription>
          "{document?.title || document?.original_name}" wordt naar de prullenbak verplaatst.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel onClick={onCancel}>Annuleren</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} className="bg-red-600 hover:bg-red-700">
          Verwijderen
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

/**
 * Document move dialog
 */
export const DocumentMoveDialog = ({
  open,
  document,
  folders,
  targetFolderId,
  onTargetChange,
  onConfirm,
  onCancel,
}) => {
  const validTargets = folders.filter(f => !f.deleted_at);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Document verplaatsen</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <p className="text-sm text-gray-600 mb-2">
            Verplaats "{document?.title || document?.original_name}" naar:
          </p>
          <Select value={targetFolderId || 'root'} onValueChange={onTargetChange}>
            <SelectTrigger>
              <SelectValue placeholder="Selecteer map" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="root">📁 Hoofdmap (root)</SelectItem>
              {validTargets.map(f => (
                <SelectItem key={f.id} value={f.id}>
                  📁 {f.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Annuleren</Button>
          <Button onClick={onConfirm}>Verplaatsen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Permanent delete confirmation dialog
 */
export const PermanentDeleteDialog = ({
  open,
  items,
  mode, // 'single' | 'bulk'
  deleting,
  onConfirm,
  onCancel,
}) => {
  const isSingle = mode === 'single';
  const itemCount = items.length;
  const title = isSingle 
    ? '🗑️ Permanent verwijderen?' 
    : `🗑️ ${itemCount} documenten permanent verwijderen?`;

  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-red-600">{title}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm text-muted-foreground">
              {isSingle && items[0] && (
                <span className="block">
                  Je staat op het punt om "{items[0].title || items[0].original_name}" definitief te verwijderen.
                </span>
              )}
              {!isSingle && (
                <span className="block">
                  Je staat op het punt om {itemCount} document{itemCount !== 1 ? 'en' : ''} definitief te verwijderen.
                </span>
              )}
              <span className="block font-semibold text-red-600">
                Dit kan niet ongedaan worden gemaakt. Het bestand wordt ook van de cloudopslag verwijderd.
              </span>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting}>Annuleren</AlertDialogCancel>
          <AlertDialogAction
            className="bg-red-600 hover:bg-red-700"
            onClick={onConfirm}
            disabled={deleting}
          >
            {deleting ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                Verwijderen...
              </>
            ) : (
              'Permanent verwijderen'
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

/**
 * Empty trash confirmation dialog
 */
export const EmptyTrashDialog = ({
  open,
  itemCount,
  deleting,
  onConfirm,
  onCancel,
}) => (
  <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle className="text-red-600">
          🧹 Prullenbak leegmaken?
        </AlertDialogTitle>
        <AlertDialogDescription asChild>
          <div className="space-y-2 text-sm text-muted-foreground">
            <span className="block">
              Je staat op het punt om alle {itemCount} items in de prullenbak definitief te verwijderen.
            </span>
            <span className="block font-semibold text-red-600">
              Dit kan niet ongedaan worden gemaakt. Alle bestanden worden ook van de cloudopslag verwijderd.
            </span>
          </div>
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel disabled={deleting}>Annuleren</AlertDialogCancel>
        <AlertDialogAction
          className="bg-red-600 hover:bg-red-700"
          onClick={onConfirm}
          disabled={deleting}
        >
          {deleting ? (
            <>
              <span className="animate-spin mr-2">⏳</span>
              Leegmaken...
            </>
          ) : (
            'Prullenbak leegmaken'
          )}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

/**
 * Document preview dialog
 */
export const PreviewDialog = ({
  open,
  previewUrl,
  document,
  onClose,
}) => (
  <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
    <DialogContent className="max-w-4xl h-[80vh]">
      <DialogHeader>
        <DialogTitle>{document?.title || document?.original_name}</DialogTitle>
      </DialogHeader>
      <div className="flex-1 min-h-0">
        {previewUrl ? (
          <iframe
            src={previewUrl}
            className="w-full h-full border rounded"
            title="Document preview"
          />
        ) : (
          <div className="flex items-center justify-center h-full text-gray-400">
            Preview laden...
          </div>
        )}
      </div>
    </DialogContent>
  </Dialog>
);

/**
 * Upload dialog
 */
export const UploadDialog = ({
  open,
  onClose,
  children,
}) => (
  <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
    <DialogContent className="max-w-2xl">
      <DialogHeader>
        <DialogTitle>📤 Bestanden uploaden</DialogTitle>
      </DialogHeader>
      {children}
    </DialogContent>
  </Dialog>
);
